import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Music2,
  Loader2,
  Clock3,
  CheckCircle2,
  PartyPopper,
  XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-white/5 border-white/10 h-12 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50";

const STATUS_META = {
  received: {
    label: "Received",
    desc: "Your request has been sent to the DJ.",
    Icon: Clock3,
    cls: "text-primary",
  },
  approved: {
    label: "Approved",
    desc: "Your request has been added to the DJ's queue.",
    Icon: CheckCircle2,
    cls: "text-green-400",
  },
  played: {
    label: "Played 🎉",
    desc: "Your song just rocked the dance floor.",
    Icon: PartyPopper,
    cls: "text-accent",
  },
  closed: {
    label: "Request Closed",
    desc: "The DJ may not be able to fit this song into the event.",
    Icon: XCircle,
    cls: "text-muted-foreground",
  },
};

const getSessionId = () => {
  let s = localStorage.getItem("de_guest_session");
  if (!s) {
    s = crypto.randomUUID();
    localStorage.setItem("de_guest_session", s);
  }
  return s;
};

export default function GuestRequest() {
  const { code } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    song_title: "",
    artist: "",
    guest_name: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [mine, setMine] = useState([]);
  const [sessionId] = useState(getSessionId);

  useEffect(() => {
    axios
      .get(`${API}/public/event/${code}`)
      .then(({ data }) => setEvent(data))
      .catch(() =>
        setError("We couldn't find this event. Double-check your QR code or link.")
      );
  }, [code]);

  const loadMine = useCallback(() => {
    axios
      .get(`${API}/public/event/${code}/my-requests`, {
        params: { session_id: sessionId },
      })
      .then(({ data }) => setMine(data))
      .catch(() => {});
  }, [code, sessionId]);

  useEffect(() => {
    loadMine();
    const t = setInterval(loadMine, 5000);
    return () => clearInterval(t);
  }, [loadMine]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/event/${code}/requests`, {
        ...form,
        session_id: sessionId,
      });
      setForm({ song_title: "", artist: "", guest_name: form.guest_name, message: "" });
      setJustSent(true);
      toast.success("Request Received. It's on its way to the DJ!");
      loadMine();
      setTimeout(() => setJustSent(false), 6000);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Could not send your request. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <main
        data-testid="guest-request-not-found"
        className="min-h-screen flex items-center justify-center px-6"
      >
        <div className="text-center">
          <img src="/images/logo.png" alt="Dream Entertainment" className="h-14 w-auto mx-auto mb-8" />
          <p className="font-serif text-3xl">{error}</p>
        </div>
      </main>
    );
  }

  if (!event) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </main>
    );
  }

  return (
    <main data-testid="guest-request-page" className="min-h-screen px-6 py-16 sm:py-24">
      <div className="max-w-xl mx-auto">
        <header className="text-center mb-12">
          <img
            src="/images/logo.png"
            alt="Dream Entertainment"
            className="h-14 sm:h-16 w-auto mx-auto"
            data-testid="guest-logo"
          />
          <Overline className="mt-10">Now Playing At</Overline>
          <h1
            data-testid="guest-event-name"
            className="font-serif text-4xl sm:text-5xl tracking-tight mt-3"
          >
            {event.name}
          </h1>
          <p className="text-muted-foreground text-sm mt-3 tracking-wide">
            {event.event_type}
            {event.dj_name ? ` · DJ ${event.dj_name}` : ""}
          </p>
        </header>

        {!event.requests_enabled ? (
          <div
            data-testid="requests-closed-notice"
            className="glass rounded-3xl p-10 text-center"
          >
            <Music2 size={36} className="text-primary mx-auto mb-4" />
            <p className="font-serif text-2xl">Requests are closed for now.</p>
            <p className="text-sm text-muted-foreground mt-2">
              The DJ may open them again soon. Hang tight.
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="song-request-form"
            className="glass rounded-3xl p-8 sm:p-10 space-y-6"
          >
            <div className="text-center">
              <Overline>Request a Song</Overline>
              {justSent && (
                <p
                  data-testid="request-received-banner"
                  className="mt-3 font-serif text-2xl text-primary"
                >
                  Request Received.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="song_title">Song Title *</Label>
              <Input
                id="song_title"
                data-testid="song-title-input"
                required
                value={form.song_title}
                onChange={(e) => setForm({ ...form, song_title: e.target.value })}
                placeholder="e.g. Temperature"
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="artist">Artist *</Label>
              <Input
                id="artist"
                data-testid="song-artist-input"
                required
                value={form.artist}
                onChange={(e) => setForm({ ...form, artist: e.target.value })}
                placeholder="e.g. Sean Paul"
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="guest_name">Your Name (optional)</Label>
              <Input
                id="guest_name"
                data-testid="guest-name-input"
                value={form.guest_name}
                onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                placeholder="So the DJ can give you a shoutout"
                className={inputCls}
              />
            </div>
            {event.allow_messages && (
              <div className="space-y-2">
                <Label htmlFor="message">Message for the DJ (optional)</Label>
                <Textarea
                  id="message"
                  data-testid="guest-message-input"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Dedication, shoutout, occasion..."
                  className="bg-white/5 border-white/10 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50"
                />
              </div>
            )}
            <button
              type="submit"
              data-testid="request-song-button"
              disabled={submitting}
              className="w-full rounded-full bg-primary text-primary-foreground py-5 text-base font-semibold tracking-[0.25em] uppercase transition-[transform,box-shadow,opacity] duration-300 hover:scale-[1.02] hover:shadow-[0_0_50px_rgba(255,0,127,0.4)] disabled:opacity-60 flex items-center justify-center gap-3"
            >
              {submitting && <Loader2 size={18} className="animate-spin" />}
              {submitting ? "Sending..." : "Request Song"}
            </button>
          </form>
        )}

        {mine.length > 0 && (
          <section className="mt-12" data-testid="my-requests-section">
            <Overline className="mb-4">Your Requests</Overline>
            <div className="space-y-3">
              {mine.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.received;
                return (
                  <div
                    key={r.id}
                    data-testid={`my-request-${r.id}`}
                    className="glass rounded-2xl p-5"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{r.song_title}</p>
                        <p className="text-sm text-muted-foreground truncate">{r.artist}</p>
                      </div>
                      <span
                        className={`shrink-0 flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase ${meta.cls}`}
                      >
                        <meta.Icon size={15} />
                        {meta.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{meta.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground/60 text-center mt-6 tracking-wider">
              Statuses update live. Only you can see your requests.
            </p>
          </section>
        )}
      </div>
    </main>
  );
}
